import React, { memo, useCallback, useState, useEffect } from 'react';
import { stockSearchService } from '../../../../config/api';

// Info Icon
const InfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// Available candle interval options
const INTERVAL_OPTIONS = [
  { value: '1m', label: '1 Minute' },
  { value: '3m', label: '3 Minutes' },
  { value: '5m', label: '5 Minutes' },
  { value: '10m', label: '10 Minutes' },
  { value: '15m', label: '15 Minutes' },
  { value: '30m', label: '30 Minutes' },
  { value: '1h', label: '1 Hour' },
  { value: '2h', label: '2 Hours' },
  { value: '3h', label: '3 Hours' },
  { value: '4h', label: '4 Hours' },
  { value: '1d', label: '1 Day' },
  { value: '1w', label: '1 Week' },
  { value: '1M', label: '1 Month' },
];

// Debounce helper
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

/**
 * BasicInfoStep - First step for entering strategy basic information
 * 
 * @param {Object} props
 * @param {Object} props.data - Current form data
 * @param {Object} props.errors - Validation errors
 * @param {Function} props.onChange - Change handler (field, value) => void
 */
const BasicInfoStep = memo(({ data, errors = {}, onChange, compact = false }) => {
  const [stockOptions, setStockOptions] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  // Debounce search input
  const debouncedSearch = useDebounce(searchInput, 300);

  // Fetch stocks when search input changes (minimum 3 characters)
  useEffect(() => {
    const fetchStocks = async () => {
      if (!debouncedSearch || debouncedSearch.length < 3) {
        setStockOptions([]);
        return;
      }

      setStockLoading(true);
      try {
        const response = await stockSearchService.search(debouncedSearch);
        const stocks = response.data || [];
        // Map API response to options format with unique key
        setStockOptions(stocks.map((stock, index) => ({
          id: stock.id || `${stock.tradingSymbol}-${stock.exchange}-${index}`,
          tradingSymbol: stock.tradingSymbol || '',
          shortName: stock.name || '',
          exchange: stock.exchange || '',
          instrumentType: stock.instrumentType || '',
        })));
      } catch (error) {
        console.error('Error fetching stocks:', error);
        setStockOptions([]);
      } finally {
        setStockLoading(false);
      }
    };

    fetchStocks();
  }, [debouncedSearch]);

  // Direct change handler - no currying to avoid creating new functions
  const handleFieldChange = useCallback((event) => {
    const { name, value } = event.target;
    onChange(name, value);
  }, [onChange]);

  const handleTickerChange = useCallback((event, newValue) => {
    // newValue can be a string (freeSolo) or an object from options
    if (typeof newValue === 'string') {
      onChange('tickerName', newValue.toUpperCase());
    } else if (newValue && newValue.tradingSymbol) {
      onChange('tickerName', newValue.tradingSymbol.toUpperCase());
    } else {
      onChange('tickerName', '');
    }
  }, [onChange]);

  const handleTickerInputChange = useCallback((event, newValue) => {
    setSearchInput(newValue || '');
  }, []);

  const renderStockDropdown = () => (
    <>
      <input
        type="text"
        className={`input input-bordered bg-base-200 w-full ${compact ? 'input-sm' : ''} ${errors.tickerName ? 'input-error' : ''}`}
        value={data.tickerName || searchInput}
        onChange={(e) => {
          handleTickerInputChange(null, e.target.value);
          handleTickerChange(null, e.target.value);
        }}
        placeholder={compact ? "Ticker" : "Type to search (e.g., REL, TCS)..."}
      />
      {stockLoading && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2">
          <span className="loading loading-spinner loading-sm"></span>
        </span>
      )}
      {stockOptions.length > 0 && searchInput.length >= 3 && (
        <ul className={`dropdown-content z-[20] menu p-2 shadow bg-base-100 rounded-box w-full max-h-60 overflow-y-auto overflow-x-hidden flex-nowrap ${compact ? 'min-w-[300px]' : ''}`}>
          {stockOptions.map((option, index) => (
            <li key={option.id || `${option.tradingSymbol}-${option.exchange}-${index}`}>
              <button
                type="button"
                className="flex items-center gap-2"
                onClick={() => handleTickerChange(null, option)}
              >
                <span className="badge badge-primary badge-outline">{option.tradingSymbol}</span>
                <div className="flex-1 text-left">
                  <span className="text-sm block truncate">{option.shortName}</span>
                  <span className="text-xs text-base-content/60">
                    {option.exchange} {option.instrumentType && `• ${option.instrumentType}`}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );

  if (compact) {
    return (
      <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-3 items-start">
        {/* Strategy Name - Span 3 */}
        <div className="lg:col-span-3 form-control w-full">
          <input
            type="text"
            name="name"
            placeholder="Strategy Name"
            className={`input input-bordered bg-base-200 input-sm w-full font-bold ${errors.name ? 'input-error' : ''}`}
            value={data.name || ''}
            onChange={handleFieldChange}
          />
          {errors.name && <span className="text-xs text-error mt-1">{errors.name}</span>}
        </div>

        {/* Ticker Symbol - Span 2 */}
        <div className="lg:col-span-2 form-control w-full relative">
          <div className="dropdown w-full">
            <input
              type="text"
              className={`input input-bordered bg-base-200 w-full input-sm ${errors.tickerName ? 'input-error' : ''}`}
              value={data.tickerName || searchInput}
              onChange={(e) => {
                handleTickerInputChange(null, e.target.value);
                handleTickerChange(null, e.target.value);
              }}
              placeholder="Ticker"
              onFocus={() => {
                // Ensure dropdown opens on focus if needed or search triggers it
              }}
            />
            {stockLoading && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2">
                <span className="loading loading-spinner loading-xs"></span>
              </span>
            )}
            {stockOptions.length > 0 && searchInput.length >= 3 && (
              <ul className="dropdown-content z-[30] menu p-2 shadow bg-base-100 rounded-box w-full max-h-60 overflow-y-auto overflow-x-hidden flex-nowrap min-w-[250px] mt-1">
                {stockOptions.map((option, index) => (
                  <li key={option.id || `${option.tradingSymbol}-${option.exchange}-${index}`}>
                    <button
                      type="button"
                      className="flex items-center gap-2 py-1"
                      onClick={() => handleTickerChange(null, option)}
                    >
                      <span className="badge badge-primary badge-outline badge-sm">{option.tradingSymbol}</span>
                      <div className="flex-1 text-left overflow-hidden">
                        <span className="text-sm block truncate">{option.shortName}</span>
                        <span className="text-[10px] text-base-content/60">
                          {option.exchange}
                        </span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {errors.tickerName && <span className="text-xs text-error mt-1">{errors.tickerName}</span>}
        </div>

        {/* Interval - Span 1 */}
        <div className="lg:col-span-1 form-control w-full">
          <select
            name="interval"
            className={`select select-bordered bg-base-200 select-sm w-full ${errors.interval ? 'select-error' : ''}`}
            value={data.interval || '1h'}
            onChange={handleFieldChange}
          >
            {INTERVAL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {errors.interval && <span className="text-xs text-error mt-1">{errors.interval}</span>}
        </div>

        {/* Dates - Span 4 */}
        <div className="lg:col-span-4 flex gap-2 w-full">
          <div className="form-control flex-1">
            <input
              type="date"
              name="startDate"
              className={`input input-bordered bg-base-200 input-sm w-full ${errors.startDate ? 'input-error' : ''}`}
              value={data.startDate || ''}
              onChange={handleFieldChange}
              max={data.endDate || undefined}
            />
            {errors.startDate && <span className="text-xs text-error mt-1">{errors.startDate}</span>}
          </div>
          <div className="form-control flex-1">
            <input
              type="date"
              name="endDate"
              className={`input input-bordered bg-base-200 input-sm w-full ${errors.endDate ? 'input-error' : ''}`}
              value={data.endDate || ''}
              onChange={handleFieldChange}
              min={data.startDate || undefined}
            />
            {errors.endDate && <span className="text-xs text-error mt-1">{errors.endDate}</span>}
          </div>
        </div>

        {/* Public Toggle - Span 2 */}
        <div className="lg:col-span-2 form-control w-full">
          <label className="label cursor-pointer justify-start gap-2 h-full py-0">
            <span className="text-xs font-bold whitespace-nowrap">Public</span>
            <input
              type="checkbox"
              className="toggle toggle-primary toggle-sm"
              checked={data.public || false}
              onChange={(e) => handleFieldChange({ target: { name: 'public', value: e.target.checked } })}
            />
          </label>
        </div>

        {/* Description - Full width row */}
        <div className="lg:col-span-12 form-control w-full">
          <textarea
            name="description"
            placeholder="Strategy description (optional)"
            className={`textarea textarea-bordered bg-base-200 textarea-sm w-full ${errors.description ? 'textarea-error' : ''}`}
            value={data.description || ''}
            onChange={handleFieldChange}
            rows={2}
          />
          {errors.description && <span className="text-xs text-error mt-1">{errors.description}</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="text-lg font-bold mb-2">Strategy Details</h2>
      <p className="text-sm text-base-content/60 mb-6">
        Enter the basic information for your trading strategy
      </p>

      <div className="space-y-4">
        {/* Strategy Name */}
        <div className="fieldset w-full">
          <label className="label">
            <span className="text-sm">Strategy Name <span className="text-error">*</span></span>
            <span className="text-xs text-base-content/60 tooltip" data-tip="A good name helps you identify this strategy later">
              <InfoIcon />
            </span>
          </label>
          <input
            type="text"
            name="name"
            placeholder="Enter strategy name..."
            className={`input input-bordered bg-base-200 w-full ${errors.name ? 'input-error' : ''}`}
            value={data.name || ''}
            onChange={handleFieldChange}
          />
          <label className="label">
            <span className={`text-xs text-base-content/60 ${errors.name ? 'text-error' : ''}`}>
              {errors.name || 'Give your strategy a unique, descriptive name'}
            </span>
          </label>
        </div>

        {/* Description */}
        <div className="fieldset w-full">
          <label className="label">
            <span className="text-sm">Description</span>
          </label>
          <textarea
            name="description"
            className={`textarea textarea-bordered bg-base-200 w-full h-24 ${errors.description ? 'textarea-error' : ''}`}
            value={data.description || ''}
            onChange={handleFieldChange}
            placeholder="e.g., Buy when RSI is oversold and price touches support line, sell when RSI is overbought"
          />
          <label className="label">
            <span className={`text-xs text-base-content/60 ${errors.description ? 'text-error' : ''}`}>
              {errors.description || 'Describe your strategy objectives and approach'}
            </span>
          </label>
        </div>

        {/* Public Strategy Toggle */}
        <div className="form-control w-full">
          <label className="label cursor-pointer justify-start gap-4">
            <span className="text-sm font-medium">Make Strategy Public</span>
            <input
              type="checkbox"
              className="toggle toggle-primary toggle-sm"
              checked={data.public || false}
              onChange={(e) => handleFieldChange({ target: { name: 'public', value: e.target.checked } })}
            />
            <span className="text-xs text-base-content/60 tooltip" data-tip="Public strategies are visible to all users in the community">
              <InfoIcon />
            </span>
          </label>
        </div>

        {/* Ticker Symbol with Autocomplete */}
        <div className="fieldset w-full">
          <label className="label">
            <span className="text-sm">Ticker Symbol <span className="text-error">*</span></span>
            <span className="text-xs text-base-content/60 tooltip" data-tip="Search for stocks by symbol or company name">
              <InfoIcon />
            </span>
          </label>
          <div className="dropdown w-full">
            {renderStockDropdown()}
          </div>
          <label className="label">
            <span className={`text-xs text-base-content/60 ${errors.tickerName ? 'text-error' : ''}`}>
              {errors.tickerName || 'Type at least 3 characters to search'}
            </span>
          </label>
        </div>

        {/* Candle Interval */}
        <div className="fieldset w-full">
          <label className="label">
            <span className="text-sm">Candle Interval <span className="text-error">*</span></span>
            <span className="text-xs text-base-content/60 tooltip" data-tip="Timeframe for each candle in the chart">
              <InfoIcon />
            </span>
          </label>
          <select
            name="interval"
            className={`select select-bordered bg-base-200 w-full ${errors.interval ? 'select-error' : ''}`}
            value={data.interval || '1h'}
            onChange={handleFieldChange}
          >
            {INTERVAL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <label className="label">
            <span className="text-xs text-base-content/60">
              Shorter intervals (1m-30m) have max 30 days history
            </span>
          </label>
          {errors.interval && <label className="label"><span className="text-xs text-error">{errors.interval}</span></label>}
        </div>

        {/* Date Range - Side by Side */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="fieldset w-full">
            <label className="label">
              <span className="text-sm">Start Date</span>
            </label>
            <input
              type="date"
              name="startDate"
              className={`input input-bordered bg-base-200 w-full ${errors.startDate ? 'input-error' : ''}`}
              value={data.startDate || ''}
              onChange={handleFieldChange}
              max={data.endDate || undefined}
            />
            <label className="label">
              <span className={`text-xs text-base-content/60 ${errors.startDate ? 'text-error' : ''}`}>
                {errors.startDate || 'Backtest start'}
              </span>
            </label>
          </div>

          <div className="fieldset w-full">
            <label className="label">
              <span className="text-sm">End Date</span>
            </label>
            <input
              type="date"
              name="endDate"
              className={`input input-bordered bg-base-200 w-full ${errors.endDate ? 'input-error' : ''}`}
              value={data.endDate || ''}
              onChange={handleFieldChange}
              min={data.startDate || undefined}
            />
            <label className="label">
              <span className={`text-xs text-base-content/60 ${errors.endDate ? 'text-error' : ''}`}>
                {errors.endDate || 'Backtest end'}
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
});

export default BasicInfoStep;
